import { useRef, useState } from 'react'

import { t } from '../../../common/i18n.js'
import { Language } from '../../../common/types/i18n.js'
import { PortalAvatar } from '../portal-avatar/portal-avatar.js'
import './profile-banner.css'

export interface ProfileBannerCompany {
    id: number
    slug: string
}

export interface ProfileBannerProps {
    name: string
    avatarUrl?: string | null
    jobTitle?: string | null
    email?: string | null
    companies?: ProfileBannerCompany[]
    status?: string
    statusLabel?: string
    tenure?: { years: number; months: number } | null
    lang: Language
    onAvatarChange?: (file: File) => Promise<boolean>
    onAvatarRemove?: () => Promise<boolean>
    vcardUrl?: string
}

const ACCEPTED_IMAGE_TYPES = '.jpg,.jpeg,.png,.webp'

const CameraIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M9.5 4L8.19 5.5H5C3.9 5.5 3 6.4 3 7.5V18.5C3 19.6 3.9 20.5 5 20.5H19C20.1 20.5 21 19.6 21 18.5V7.5C21 6.4 20.1 5.5 19 5.5H15.81L14.5 4H9.5ZM12 17.5C9.79 17.5 8 15.71 8 13.5C8 11.29 9.79 9.5 12 9.5C14.21 9.5 16 11.29 16 13.5C16 15.71 14.21 17.5 12 17.5ZM12 11.5C10.9 11.5 10 12.4 10 13.5C10 14.6 10.9 15.5 12 15.5C13.1 15.5 14 14.6 14 13.5C14 12.4 13.1 11.5 12 11.5Z"
            fill="currentColor"
        />
    </svg>
)

const ContactIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V6h16v12zM4 0h16v2H4zm0 22h16v2H4zm8-10c1.38 0 2.5-1.12 2.5-2.5S13.38 7 12 7 9.5 8.12 9.5 9.5 10.62 12 12 12zm0 1c-1.68 0-5 .84-5 2.5V17h10v-1.5c0-1.66-3.32-2.5-5-2.5z"
            fill="currentColor"
        />
    </svg>
)

const CloseIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M18 6L6 18M6 6l12 12"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
)

export const ProfileBanner = ({
    name,
    avatarUrl,
    jobTitle,
    email,
    companies,
    status,
    statusLabel,
    tenure,
    lang,
    onAvatarChange,
    onAvatarRemove,
    vcardUrl,
}: ProfileBannerProps) => {
    const ph = t.portal
    const fileRef = useRef<HTMLInputElement>(null)
    const [isUploading, setIsUploading] = useState(false)
    const [previewUrl, setPreviewUrl] = useState<string | null>(null)
    const [popoverOpen, setPopoverOpen] = useState(false)

    const canEdit = !!onAvatarChange
    const shownUrl = previewUrl ?? avatarUrl

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file || !onAvatarChange) return

        const localUrl = URL.createObjectURL(file)
        setPreviewUrl(localUrl)
        setIsUploading(true)

        try {
            const ok = await onAvatarChange(file)
            if (!ok) setPreviewUrl(null)
        } finally {
            setIsUploading(false)
            if (fileRef.current) fileRef.current.value = ''
        }
    }

    const handleRemove = async () => {
        if (!onAvatarRemove) return
        setIsUploading(true)
        try {
            const ok = await onAvatarRemove()
            if (ok) setPreviewUrl(null)
        } finally {
            setIsUploading(false)
            setPopoverOpen(false)
        }
    }

    return (
        <div className="profile-banner">
            <div className="profile-banner__avatar-wrap">
                <button
                    type="button"
                    className="profile-banner__avatar-btn"
                    onClick={canEdit ? () => setPopoverOpen(!popoverOpen) : undefined}
                    style={canEdit ? undefined : { cursor: 'default' }}
                >
                    <PortalAvatar name={name} avatarUrl={shownUrl} px={80} />
                    {canEdit && (
                        <span className="profile-banner__camera-badge">
                            <CameraIcon />
                        </span>
                    )}
                </button>

                {canEdit && (
                    <input
                        ref={fileRef}
                        type="file"
                        accept={ACCEPTED_IMAGE_TYPES}
                        className="profile-banner__file-input"
                        onChange={handleFileSelect}
                    />
                )}
            </div>

            <div className="profile-banner__info">
                <h2 className="profile-banner__name">{name}</h2>
                <div className="profile-banner__title-row">
                    {jobTitle && <span className="profile-banner__job-title">{jobTitle}</span>}
                    {companies?.map((company) => (
                        <span key={company.id} className="profile-banner__company-badge">
                            {company.slug}
                        </span>
                    ))}
                    {status && statusLabel && (
                        <span
                            className={`profile-banner__status-badge profile-banner__status-badge--${status}`}
                        >
                            {statusLabel}
                        </span>
                    )}
                    {tenure && (tenure.years > 0 || tenure.months > 0) && (
                        <span className="profile-banner__tenure-badge">
                            {t.common.tenure(tenure.years, tenure.months)(lang)}
                        </span>
                    )}
                </div>
                {email && <div className="profile-banner__email">{email}</div>}
            </div>

            {vcardUrl && (
                <a
                    href={vcardUrl}
                    className="profile-banner__vcard-btn"
                    title={t.portal.profile.saveContact(lang)}
                    download
                >
                    <ContactIcon />
                    <span>{t.portal.profile.saveContact(lang)}</span>
                </a>
            )}

            {popoverOpen && (
                <>
                    <div
                        className="profile-banner__modal-overlay"
                        onClick={() => setPopoverOpen(false)}
                    />
                    <div className="profile-banner__modal">
                        <button
                            type="button"
                            className="profile-banner__modal-close"
                            onClick={() => setPopoverOpen(false)}
                        >
                            <CloseIcon />
                        </button>
                        {shownUrl ? (
                            <>
                                <img
                                    src={shownUrl}
                                    alt={name}
                                    className="profile-banner__modal-img"
                                />
                                <p className="profile-banner__popover-desc">
                                    {ph.profile.avatarPopoverHasImage(lang)}
                                </p>
                                <p className="profile-banner__popover-limit">
                                    {ph.profile.avatarPopoverMaxSize(lang)}
                                </p>
                                <div className="profile-banner__popover-actions">
                                    <button
                                        type="button"
                                        className="profile-banner__popover-btn profile-banner__popover-btn--primary"
                                        disabled={isUploading}
                                        onClick={() => fileRef.current?.click()}
                                    >
                                        {isUploading
                                            ? ph.profile.settingsAvatarUploading(lang)
                                            : ph.profile.avatarPopoverChange(lang)}
                                    </button>
                                    {onAvatarRemove && (
                                        <button
                                            type="button"
                                            className="profile-banner__popover-btn profile-banner__popover-btn--secondary"
                                            disabled={isUploading}
                                            onClick={handleRemove}
                                        >
                                            {ph.profile.avatarPopoverRemove(lang)}
                                        </button>
                                    )}
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="profile-banner__popover-empty">
                                    <PortalAvatar name={name} px={120} />
                                </div>
                                <p className="profile-banner__popover-desc">
                                    {ph.profile.avatarPopoverNoImage(lang)}
                                </p>
                                <p className="profile-banner__popover-limit">
                                    {ph.profile.avatarPopoverMaxSize(lang)}
                                </p>
                                <div className="profile-banner__popover-actions">
                                    <button
                                        type="button"
                                        className="profile-banner__popover-btn profile-banner__popover-btn--primary"
                                        disabled={isUploading}
                                        onClick={() => fileRef.current?.click()}
                                    >
                                        {isUploading
                                            ? ph.profile.settingsAvatarUploading(lang)
                                            : ph.profile.avatarPopoverChoose(lang)}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </>
            )}
        </div>
    )
}
